import React from 'react';
import { isMcpTool } from '@kentik/ui-shared/kaizen/constants';
import DataExplorerToolResult from './toolResults/DataExplorerToolResult';
import NmsToolResult from './toolResults/NmsToolResult';
import WebSearchToolResult from './toolResults/WebSearchToolResult';
import WhoisToolResult from './toolResults/WhoisToolResult';
import AlertToolResult from './toolResults/AlertToolResult';
import SyntheticsToolResult from './toolResults/SyntheticsToolResult';
import SyntheticsTestResultsToolResult from './toolResults/SyntheticsTestResultsToolResult';
import SyntheticsTraceResultsToolResult from './toolResults/SyntheticsTraceResultsToolResult';
import DeviceInventoryToolResult from './toolResults/DeviceInventoryToolResult';
import DeviceConnectionsToolResult from './toolResults/DeviceConnectionsToolResult';
import DeviceConfigToolResult from './toolResults/DeviceConfigToolResult';
import KbToolResult from './toolResults/KbToolResult';
import SyslogToolResult from './toolResults/SyslogToolResult';
import DefaultToolResult from './toolResults/DefaultToolResult';
import PathFinderToolResult from './toolResults/PathFinderToolResult';
import InterfaceInventoryToolResult from './toolResults/InterfaceInventoryToolResult';
import SiteInventoryToolResult from './toolResults/SiteInventoryToolResult';
import CustomApplicationsToolResult from './toolResults/CustomApplicationsToolResult';
import ConnectivityTestToolResult from './toolResults/ConnectivityTestToolResult';
import AWSInventoryToolResult from './toolResults/cloud/AwsInventoryToolResult';
import AzureInventoryToolResult from './toolResults/cloud/AzureInventoryToolResult';
import AzureResourceGraphToolResult from './toolResults/cloud/AzureResourceGraphToolResult';
import GcpInventoryToolResult from './toolResults/cloud/GcpInventoryToolResult';
import OciInventoryToolResult from './toolResults/cloud/OciInventoryToolResult';
import DeviceCommandToolResult from './toolResults/DeviceCommandToolResult';
import RunbookToolResult from './toolResults/RunbookToolResult';
import UdeToolResult from './toolResults/UdeToolResult';
import PeeringDbToolResult from './toolResults/PeeringDbToolResult';
import ExportPdfToolResult from './toolResults/ExportPdfToolResult';
import SubAgentToolResult from './toolResults/SubAgentToolResult';
import SitesToolResult from './toolResults/SitesToolResult';

const SUB_AGENT_TOOLS = new Set(['math_agent', 'synthetics_agent', 'peeringdb_agent']);
export const isSubAgentTool = (toolName) => SUB_AGENT_TOOLS.has(toolName) || isMcpTool(toolName);

// Human-readable display names for tools
const TOOL_DISPLAY_NAMES = {
  data_explorer: 'Data Explorer',
  snmp_trap: 'SNMP Trap',
  nms: 'NMS',
  alerts_tool: 'Alerts',
  device_inventory: 'Device Inventory',
  device_connections: 'Device Connections',
  device_config: 'Device Config',
  interface_inventory: 'Interface Inventory',
  site_inventory: 'Site Inventory',
  custom_applications: 'Custom Applications',
  knowledge_base: 'Knowledge Base',
  synthetics_test_status: 'Synthetics Test Status',
  synthetics_test_results: 'Synthetics Test Results',
  synthetics_trace_results: 'Synthetics Trace Results',
  synthetics_alarms: 'Synthetics Alarms',
  synthetics_test_config: 'Synthetics Test Config',
  'connectivity-test': 'Connectivity Test',
  web_search: 'Web Search',
  whois: 'Whois',
  a2a: 'A2A',
  syslog: 'Syslog',
  aws_inventory: 'AWS Inventory',
  azure_inventory: 'Azure Inventory',
  azure_resource_graph_query: 'Azure Resource Graph',
  device_command: 'Device Command',
  runbooks: 'Runbooks',
  cloud_pathfinder: 'Path Finder',
  math_agent: 'Math Agent',
  synthetics_agent: 'Synthetics Agent',
  ott_inventory: 'OTT Inventory',
  peeringdb_agent: 'PeeringDB Agent'
};

export function getToolDisplayName(toolName) {
  if (!toolName) {
    return 'Unknown Tool';
  }
  if (TOOL_DISPLAY_NAMES[toolName]) {
    return TOOL_DISPLAY_NAMES[toolName];
  }
  return toolName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Tool name constants
export const TOOL_NAMES = {
  DATA_EXPLORER: 'data_explorer',
  SNMP_TRAP: 'snmp_trap',
  NMS: 'nms',
  ALERTS: 'alerts_tool',
  DEVICE_INVENTORY: 'device_inventory',
  DEVICE_CONNECTIONS: 'device_connections',
  DEVICE_CONFIG: 'device_config',
  INTERFACE_INVENTORY: 'interface_inventory',
  SITE_INVENTORY: 'site_inventory',
  CUSTOM_APPLICATIONS: 'custom_applications',
  KNOWLEDGE_BASE: 'knowledge_base',
  SYNTHETICS: 'synthetics_test_status',
  SYNTHETICS_TEST_RESULTS: 'synthetics_test_results',
  SYNTHETICS_TRACE_RESULTS: 'synthetics_trace_results',
  CONNECTIVITY_TEST: 'connectivity-test',
  WEB_SEARCH: 'web_search',
  WHOIS: 'whois',
  A2A: 'a2a',
  SYSLOG: 'syslog',
  AWS_INVENTORY: 'aws_inventory',
  AZURE_INVENTORY: 'azure_inventory',
  AZURE_RESOURCE_GRAPH: 'azure_resource_graph_query',
  GCP_INVENTORY: 'gcp_inventory',
  OCI_INVENTORY: 'oci_inventory',
  DEVICE_COMMAND: 'device_command',
  RUNBOOKS: 'runbooks',
  UDE_QUERY: 'ude_query',
  EXPORT_PDF: 'export_pdf',
  PEERINGDB: 'peeringdb',
  SITES: 'sitestool',
  OTT_INVENTORY: 'ott_inventory',

  // ---Sub-agent tools---
  MATH_AGENT: 'math_agent',
  SYNTHETICS_MONITORING_AGENT: 'synthetics_agent',
  PEERINGDB_AGENT: 'peeringdb_agent',

  // ---Cloud tools---
  PATH_FINDER: 'cloud_pathfinder'
};

/**
 * Internal tool (operation) names emitted by the PeeringDB sub-agent. Each one
 * corresponds to a single PeeringDB operation and matches a case in
 * PeeringDbToolResult's per-operation renderer, so these child tool calls render
 * with the rich PeeringDB view rather than the default text result.
 */
export const PEERINGDB_OPERATION_TOOLS = [
  'has_peeringdb_record',
  'common_ix',
  'ix_at_my_facilities',
  'list_ix_by_geo',
  'list_ix_by_geofence',
  'common_facility',
  'get_ix_details',
  'get_fac_details',
  'get_asn_info',
  'get_asn_summary',
  'search_ix_by_name',
  'search_fac_by_name',
  'list_asn_for_ix',
  'list_asn_for_fac',
  'list_fac_by_geo',
  'list_fac_for_ix',
  'list_ix_for_fac',
  'asn_ips_at_ix',
  'get_asn_contacts'
];

/**
 * Factory function to create the appropriate tool result component
 * based on the tool type. This centralizes the logic and makes it
 * reusable across different contexts (panel, inline, etc.)
 */
export const createToolResultComponent = (tool, props = {}) => {
  const {
    onCloseToolResult,
    onDataViewCreate,
    onQueryError,
    chartHeight = 500,
    isInlineMode = false,
    ...otherProps
  } = props;

  const toolResultProps = {
    key: tool.id || tool.tool_tracking_id,
    tool,
    onCloseToolResult,
    isInlineMode,
    ...otherProps
  };

  // PeeringDB sub-agent operations share a single rich renderer.
  if (PEERINGDB_OPERATION_TOOLS.includes(tool.tool_name)) {
    return <PeeringDbToolResult {...toolResultProps} />;
  }

  if (isSubAgentTool(tool.tool_name)) {
    return <SubAgentToolResult {...toolResultProps} />;
  }

  switch (tool.tool_name) {
    case TOOL_NAMES.DATA_EXPLORER:
      return (
        <DataExplorerToolResult
          {...toolResultProps}
          onDataViewCreate={onDataViewCreate}
          onQueryError={onQueryError}
          chartHeight={chartHeight}
        />
      );
    case TOOL_NAMES.SNMP_TRAP:
      return (
        <DataExplorerToolResult
          {...toolResultProps}
          onDataViewCreate={onDataViewCreate}
          onQueryError={onQueryError}
          chartHeight={chartHeight}
        />
      );
    case TOOL_NAMES.NMS:
      return <NmsToolResult {...toolResultProps} chartHeight={chartHeight} />;
    case TOOL_NAMES.ALERTS:
      return <AlertToolResult {...toolResultProps} />;
    case TOOL_NAMES.DEVICE_INVENTORY:
      return <DeviceInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.DEVICE_CONNECTIONS:
      return <DeviceConnectionsToolResult {...toolResultProps} />;
    case TOOL_NAMES.DEVICE_CONFIG:
      return <DeviceConfigToolResult {...toolResultProps} />;
    case TOOL_NAMES.INTERFACE_INVENTORY:
      return <InterfaceInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.SITE_INVENTORY:
      return <SiteInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.CUSTOM_APPLICATIONS:
      return <CustomApplicationsToolResult {...toolResultProps} />;
    case TOOL_NAMES.KNOWLEDGE_BASE:
      return <KbToolResult {...toolResultProps} />;
    case TOOL_NAMES.SYNTHETICS:
      return <SyntheticsToolResult {...toolResultProps} />;
    case TOOL_NAMES.SYNTHETICS_TEST_RESULTS:
      return <SyntheticsTestResultsToolResult {...toolResultProps} />;
    case TOOL_NAMES.SYNTHETICS_TRACE_RESULTS:
      return <SyntheticsTraceResultsToolResult {...toolResultProps} />;
    case TOOL_NAMES.CONNECTIVITY_TEST:
      return <ConnectivityTestToolResult {...toolResultProps} />;
    case TOOL_NAMES.WEB_SEARCH:
      return <WebSearchToolResult {...toolResultProps} />;
    case TOOL_NAMES.WHOIS:
      return <WhoisToolResult {...toolResultProps} />;
    case TOOL_NAMES.SITES:
      return <SitesToolResult {...toolResultProps} />;
    case TOOL_NAMES.AWS_INVENTORY:
      return <AWSInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.AZURE_INVENTORY:
      return <AzureInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.AZURE_RESOURCE_GRAPH:
      return <AzureResourceGraphToolResult {...toolResultProps} />;
    case TOOL_NAMES.GCP_INVENTORY:
      return <GcpInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.OCI_INVENTORY:
      return <OciInventoryToolResult {...toolResultProps} />;
    case TOOL_NAMES.SYSLOG:
      return (
        <SyslogToolResult
          {...toolResultProps}
          onDataViewCreate={onDataViewCreate}
          onQueryError={onQueryError}
          chartHeight={chartHeight}
        />
      );
    case TOOL_NAMES.DEVICE_COMMAND:
      return <DeviceCommandToolResult {...toolResultProps} />;
    case TOOL_NAMES.RUNBOOKS:
      return <RunbookToolResult {...toolResultProps} />;
    case TOOL_NAMES.UDE_QUERY:
      return <UdeToolResult {...toolResultProps} chartHeight={chartHeight} />;
    case TOOL_NAMES.PEERINGDB:
      return <PeeringDbToolResult {...toolResultProps} />;
    case TOOL_NAMES.EXPORT_PDF:
      return <ExportPdfToolResult {...toolResultProps} />;
    case TOOL_NAMES.PATH_FINDER:
      return <PathFinderToolResult {...toolResultProps} />;
    default:
      return <DefaultToolResult {...toolResultProps} />;
  }
};

const TOOL_RESULT_VIEW_SET = new Set([
  TOOL_NAMES.DATA_EXPLORER,
  TOOL_NAMES.NMS,
  TOOL_NAMES.KNOWLEDGE_BASE,
  TOOL_NAMES.ALERTS,
  TOOL_NAMES.SYNTHETICS,
  TOOL_NAMES.SYNTHETICS_TEST_RESULTS,
  TOOL_NAMES.SYNTHETICS_TRACE_RESULTS,
  TOOL_NAMES.CONNECTIVITY_TEST,
  TOOL_NAMES.WEB_SEARCH,
  TOOL_NAMES.WHOIS,
  TOOL_NAMES.A2A,
  TOOL_NAMES.INTERFACE_INVENTORY,
  TOOL_NAMES.DEVICE_INVENTORY,
  TOOL_NAMES.DEVICE_CONNECTIONS,
  TOOL_NAMES.DEVICE_CONFIG,
  TOOL_NAMES.SITE_INVENTORY,
  TOOL_NAMES.CUSTOM_APPLICATIONS,
  TOOL_NAMES.SNMP_TRAP,
  TOOL_NAMES.SYSLOG,
  TOOL_NAMES.AWS_INVENTORY,
  TOOL_NAMES.AZURE_INVENTORY,
  TOOL_NAMES.AZURE_RESOURCE_GRAPH,
  TOOL_NAMES.OCI_INVENTORY,
  TOOL_NAMES.GCP_INVENTORY,
  TOOL_NAMES.DEVICE_COMMAND,
  TOOL_NAMES.RUNBOOKS,
  TOOL_NAMES.UDE_QUERY,
  TOOL_NAMES.PATH_FINDER,
  TOOL_NAMES.EXPORT_PDF,
  TOOL_NAMES.PEERINGDB,
  TOOL_NAMES.PEERINGDB_AGENT,
  TOOL_NAMES.MATH_AGENT,
  TOOL_NAMES.SYNTHETICS_MONITORING_AGENT,
  TOOL_NAMES.SITES
]);

export const hasToolResultView = (toolName) =>
  TOOL_RESULT_VIEW_SET.has(toolName) || PEERINGDB_OPERATION_TOOLS.includes(toolName) || isSubAgentTool(toolName);
